import { type Row, number, ratio, text } from '../CommercialKpiTracker/liveModel';

export const PAGE_ID = 'countries';
export const VIEWS = [
  'Countries Margin',
  'Countries CR',
  'Product Margin',
  'Products CR',
  'Destinations Margin',
] as const;
export const METRICS = [
  ['margin', 'Margin'],
  ['sales', 'TTV'],
  ['estimated', 'Estimated margin'],
  ['aov', 'AoV (BR)'],
  ['orders', 'Orders (BR)'],
  ['customers', 'Customers (BR)'],
] as const;
export const SALES_FILTERS = [
  ['market', 'Country'],
  ['country_group', 'Country group'],
  ['product', 'Product'],
  ['destination', 'Destination'],
  ['cancelled', 'Cancelled'],
  ['buyer', 'New / Repeat'],
  ['language', 'Website language'],
  ['country_panel', 'Selected country panel'],
  ['product_group', 'Selected product panel'],
  ['destination_group', 'Selected destination panel'],
] as const;
export const CR_FILTERS = [
  ['website_region', 'Website region'],
  ['cr_product', 'Product'],
  ['language', 'Website language'],
] as const;
export const KEYS = [...new Set([...SALES_FILTERS, ...CR_FILTERS].map(([k]) => k))];
export const addDays = (d: string, n: number) => new Date(Date.parse(d) + n * 86400000).toISOString().slice(0, 10);
export function yesterday(now = new Date()) {
  return addDays(now.toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' }), -1);
}
export function startOfPeriod(period: string, to: string) {
  return period === 'Yesterday'
    ? to
    : period === 'MTD'
      ? to.slice(0, 8) + '01'
      : period === 'YTD'
        ? to.slice(0, 4) + '-01-01'
        : addDays(to, period === 'Last 14 days' ? -13 : period === 'Last 28 days' ? -27 : -6);
}
export function metricValue(row: Row, metric: string, basis: string, ly = false): number | null {
  if (metric === 'aov') {
    return ratio(number(row[ly ? 'ly_aov_sales' : 'aov_sales']), number(row[ly ? 'ly_aov_orders' : 'aov_orders']));
  }
  if (metric === 'orders' || metric === 'customers') {
    const v = number(row[metric + (ly ? '_ly' : '')]);
    return v === 0 ? null : v;
  }
  return number(row[`${metric}_${ly ? 'ly' : ''}${basis.toLowerCase()}`]);
}
export function metricsRow(row: Row, metric: string, basis: string): Row {
  const current = metricValue(row, metric, basis),
    previous = metricValue(row, metric, basis, true),
    r = ratio(current, previous);
  return { ...row, current, previous, yoy: r === null ? null : r - 1 };
}
export function comparisonUnavailable(rows: Array<Row>, metric: string, basis: string): boolean {
  return (
    rows.some((row) => metricValue(row, metric, basis) !== null) &&
    rows.every((row) => metricValue(row, metric, basis, true) === null)
  );
}
export function trendRows(
  rows: Array<Row>,
  dimension: string,
  metric: string,
  basis: string,
  days: number,
  cr: boolean,
): Array<Row> {
  const groups = new Map<string, Array<Row>>();
  for (const r of rows) {
    const key = dimension ? text(r[dimension]) : 'Overall';
    groups.set(key, [...(groups.get(key) ?? []), { ...r, date: text(r.date ?? r['day(date)']).slice(0, 10) }]);
  }
  const output: Array<Row> = [];
  for (const [group, raw] of groups) {
    const sorted = raw.sort((a, b) => text(a.date).localeCompare(text(b.date)));
    const byDate = new Map(sorted.map((r) => [text(r.date), r]));
    if (!sorted.length) {
      continue;
    }
    for (let date = text(sorted[0].date); date <= text(sorted[sorted.length - 1].date); date = addDays(date, 1)) {
      const r = byDate.get(date),
        window: Array<Row> = [];
      for (let i = 0; i < days; i++) {
        const item = byDate.get(addDays(date, -i));
        if (item) {
          window.push(item);
        }
      }
      const average = (ly: boolean) => {
        const vs = window.map((x) => metricValue(x, metric, basis, ly)).filter((v): v is number => v !== null);
        return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null;
      };
      const current = cr ? ratio(number(r?.packages), number(r?.views)) : average(false),
        previous = cr ? null : average(true),
        rate = ratio(current, previous);
      output.push({
        group,
        date,
        current,
        previous,
        yoy: rate === null ? null : rate - 1,
        packages: r?.packages,
        views: r?.views,
      });
    }
  }
  return output;
}
